import type { ComputedRef, InjectionKey, Ref } from "vue";
import {
  type EventListEventFieldsFragment,
  type ArchiveRange,
  type IFlagConnection,
  EventType,
  SQDCTileEventTypes,
  EisenhowerPriority,
} from "@/graphql";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";

export type SQDCActionDeadlineChange = {
  actionId: number;
  changedById: number;
  changedByName: string;
  changedAt: string;
};

// This is used to determine the current open boardId
export const BoardIdInjectionKey = Symbol("BoardIdInjectionKey") as InjectionKey<
  Ref<number | null>
>;

/****** Everything below here must be evaluated and cleaned up ******/

// This is a map of flag ids that is included by the board and it is used to highlight the flags in the event list (can be loaded from cache)
export const BoardIncludedFlagIdsInjectionKey = Symbol("BoardIdInjectionKey") as InjectionKey<
  Ref<number[] | null>
>;

// Needs to be cleaned up (removed)
export const TechnicalPlaceAndCategoryInjectionKey = Symbol(
  "TechnicalPlaceAndCategoryInjectionKey",
) as InjectionKey<
  ComputedRef<
    | undefined
    | {
        technicalPlaceId?: number;
        technicalCategoryId?: number;
      }
  >
>;

export const EventsInjectionKey = Symbol("EventsInjectionKey") as InjectionKey<
  Readonly<Ref<Readonly<EventListEventFieldsFragment[]>>>
>;

export const FilteredEventsInjectionKey = Symbol("FilteredEventsInjectionKey") as InjectionKey<
  ComputedRef<{
    activeEvents: EventListEventFieldsFragment[];
    numberOf: {
      events: number;
      actions: number;
    };
  }>
>;

export const EABEventInjectionKey = Symbol("EABEventInjectionKey") as InjectionKey<
  ComputedRef<EventListEventFieldsFragment>
>;

// ?????
export const TechnicalPlaceForEventModalInjectionKey = Symbol(
  "TechnicalPlaceForEventModalInjectionKey",
) as InjectionKey<ComputedRef<{ id?: number; parentId?: number }>>;

export const CostCenterForEventModalInjectionKey = Symbol(
  "CostCenterForEventModalInjectionKey",
) as InjectionKey<ComputedRef<{ id?: number; parentId?: number }>>;

export const CommentUserIdInjectionKey = Symbol("CommentUserIdInjectionKey") as InjectionKey<
  Ref<number | undefined>
>;

// IHOrder hack for tkmpe needs to be removed and implemented under workflow
export const CommonTaskAdditionalComment = Symbol("CommonTaskAdditionalComment") as InjectionKey<
  Ref<string | null>
>;

// Determines if the board is showing the archive to prevent transparent overlay
export const ArchiveRangeKey = Symbol("ArchiveRangeKey") as InjectionKey<
  Ref<ArchiveRange | undefined>
>;

export const EventActionButtonsDisabledKey = Symbol(
  "EventActionButtonsDisabledKey",
) as InjectionKey<Ref<boolean>>;

export const DefaultFlagInjectionKey = Symbol("DefaultFlagInjectionKey") as InjectionKey<
  Ref<IFlagConnection[]>
>;

export const DefaultEisenhowerPriorityInjectionKey = Symbol(
  "DefaultEisenhowerPriorityInjectionKey",
) as InjectionKey<Ref<EisenhowerPriority>>;

export const fallbackEisenhowerPriority = EisenhowerPriority.NO_PRIORITY;

export const DefaultDateInjectionKey = Symbol("DefaultDateInjectionKey") as InjectionKey<
  ComputedRef<{ isoString: string; editable: boolean }>
>;

export const ReloadBoardInjectionKey = Symbol("ReloadBoardInjectionKey") as InjectionKey<
  () => void
>;

export const EABSystemSettingsInjectionKey = Symbol(
  "EABSystemSettingsInjectionKey",
) as InjectionKey<
  ComputedRef<
    | {
        canArchiveWithoutActions: boolean;
        delegateEscalateRequired: boolean;
      }
    | undefined
  >
>;

export const TaskModalCommentIsRequiredInjectionKey = Symbol(
  "TaskModalCommentIsRequiredInjectionKey",
) as InjectionKey<Ref<boolean>>;

export enum RecentlyChangedAlternativeEnum {
  NORMAL,
  GREEN_WAVES,
}

export function getRecentlyChangedAlternative(): RecentlyChangedAlternativeEnum {
  if (isModifierSet(ModifierType.idHighlightWaves)) {
    return RecentlyChangedAlternativeEnum.GREEN_WAVES;
  }
  return RecentlyChangedAlternativeEnum.NORMAL;
}

export const EventTypesInjectionKey = Symbol("EventTypesInjectionKey") as InjectionKey<
  Ref<EventType[]>
>;

export const SQDCTileEventTypesInjectionKey = Symbol(
  "SQDCTileEventTypesInjectionKey",
) as InjectionKey<Ref<SQDCTileEventTypes[]>>;

export const SQDCTileEventActionTypeIdsInjectionKey = Symbol(
  "SQDCTileEventActionTypeIdsInjectionKey",
) as InjectionKey<Ref<number[]>>;

export const SQDCTileEventShorthandIdsInjectionKey = Symbol(
  "SQDCTileEventShorthandIdsInjectionKey",
) as InjectionKey<Ref<number[]>>;

// Is it possible to remove this?
export const BoardIsLoadingInjectionKey = Symbol("BoardIsLoadingInjectionKey") as InjectionKey<
  Ref<boolean>
>;

export const SQDCActionDeadlineChangesInjectionKey = Symbol(
  "SQDCActionDeadlineChangesInjectionKey",
) as InjectionKey<ComputedRef<ReadonlyMap<number, readonly SQDCActionDeadlineChange[]>>>;
